import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { getRecentNotifications } from '../services/DashboardServices';
import './ActivityNotifications.css';

const MAX_ITEMS = 10;
const POLL_MS = 8000;
const PLANT_ROTATE_MS = 10000;

function kindClass(kind) {
  switch (kind) {
    case 'login':
      return 'notif-kind-login';
    case 'create':
      return 'notif-kind-create';
    case 'edit':
      return 'notif-kind-edit';
    case 'delete':
      return 'notif-kind-delete';
    default:
      return 'notif-kind-activity';
  }
}

function kindLabel(kind) {
  switch (kind) {
    case 'login':
      return 'Login';
    case 'create':
      return 'Create';
    case 'edit':
      return 'Edit';
    case 'delete':
      return 'Delete';
    default:
      return 'Update';
  }
}

/**
 * Merge activity notifications into FIFO queue (max 10).
 * Newest at top; drop oldest from bottom when over capacity.
 */
function mergeNotifications(prev, incoming) {
  const activityIncoming = (incoming || []).filter((n) => n.kind !== 'waiting_checkin');
  const seen = new Set((prev || []).map((n) => n.id));
  const additions = [];
  activityIncoming.forEach((item) => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    additions.push({ ...item, _enter: true });
  });

  if (!additions.length) {
    return { items: prev || [], changed: false };
  }

  return {
    items: [...additions, ...(prev || [])].slice(0, MAX_ITEMS),
    changed: true,
  };
}

function pendingSignature(pendingGroups) {
  return (pendingGroups || [])
    .map((g) => `${g.plant_id}:${(g.vehicles || []).map((v) => v.id).join(',')}`)
    .join('|');
}

function ActivityNotifications({ cookies, plantId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pendingGroups, setPendingGroups] = useState([]);
  const [plantIndex, setPlantIndex] = useState(0);
  const [slidePulse, setSlidePulse] = useState(0);
  const [error, setError] = useState('');
  const prevSignature = useRef('');
  const prevPendingSig = useRef('');
  const listRef = useRef(null);
  const pauseRotation = useRef(false);

  const pendingTotal = useMemo(
    () => (pendingGroups || []).reduce((sum, g) => sum + (g.count || g.vehicles?.length || 0), 0),
    [pendingGroups],
  );
  const badgeCount = items.length + pendingTotal;

  const activePlantGroup = useMemo(() => {
    if (!pendingGroups.length) return null;
    const index = plantIndex % pendingGroups.length;
    return pendingGroups[index] || pendingGroups[0];
  }, [pendingGroups, plantIndex]);

  const showPopup = useCallback(() => setOpen(true), []);
  const hidePopup = useCallback(() => setOpen(false), []);

  // Keep plant index valid when groups refresh
  useEffect(() => {
    if (!pendingGroups.length) {
      setPlantIndex(0);
      return;
    }
    setPlantIndex((idx) => (idx >= pendingGroups.length ? 0 : idx));
  }, [pendingGroups]);

  // Rotate plant every 10 seconds only while panel is manually open
  useEffect(() => {
    if (!open || pendingGroups.length <= 1) return undefined;
    const timer = setInterval(() => {
      if (pauseRotation.current) return;
      setPlantIndex((idx) => (idx + 1) % pendingGroups.length);
      setSlidePulse((p) => p + 1);
    }, PLANT_ROTATE_MS);
    return () => clearInterval(timer);
  }, [open, pendingGroups.length]);

  const applyIncoming = useCallback((incoming, pending) => {
    const nextPending = pending || [];
    const pendingSig = pendingSignature(nextPending);
    if (pendingSig !== prevPendingSig.current) {
      prevPendingSig.current = pendingSig;
      setSlidePulse((p) => p + 1);
    }
    setPendingGroups(nextPending);

    setItems((prev) => {
      const result = mergeNotifications(prev, incoming);
      const merged = result.items;
      const signature = merged.map((n) => n.id).join('|');
      if (signature !== prevSignature.current) {
        prevSignature.current = signature;
        setSlidePulse((p) => p + 1);
      }
      return merged;
    });
  }, []);

  const load = useCallback(() => {
    if (!cookies?.myToken) return;
    getRecentNotifications(cookies, plantId, 20)
      .then((data) => {
        setError('');
        applyIncoming(data.notifications || [], data.pending_checkins || []);
      })
      .catch((err) => {
        // Expired session — interceptor clears auth; avoid noisy UI / overlay
        if (err?.response?.status === 401 || err?.__authHandled) {
          setError('');
          return;
        }
        setError(err?.response?.data?.detail || err?.message || 'Failed to load notifications');
      });
  }, [cookies, plantId, applyIncoming]);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_MS);
    const onFocus = () => load();
    const onActivity = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('visibilitychange', onFocus);
    window.addEventListener('appActivityUpdated', onActivity);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('appActivityUpdated', onActivity);
    };
  }, [load]);

  useEffect(() => {
    if (!listRef.current || !open) return undefined;
    const el = listRef.current;
    el.classList.remove('notif-slide-tick');
    void el.offsetWidth;
    el.classList.add('notif-slide-tick');
    const timer = setTimeout(() => {
      setItems((prev) => prev.map((n) => (n._enter ? { ...n, _enter: false } : n)));
    }, 500);
    return () => clearTimeout(timer);
  }, [slidePulse, open, plantIndex]);

  if (!cookies?.myToken) return null;

  const hasContent = pendingTotal > 0 || items.length > 0;
  const plantPosition = pendingGroups.length
    ? `${(plantIndex % pendingGroups.length) + 1}/${pendingGroups.length}`
    : '';

  return (
    <div
      className={`activity-notifications-popup ${open ? 'is-open' : 'is-closed'}`}
      onMouseEnter={() => {
        pauseRotation.current = true;
      }}
      onMouseLeave={() => {
        pauseRotation.current = false;
      }}
    >
      {!open && (
        <button
          type="button"
          className="activity-notifications-peek"
          onClick={showPopup}
          title="Show notifications"
          aria-label="Show notifications"
        >
          <BiChevronLeft size={22} />
          {badgeCount > 0 && (
            <span className="activity-notifications-peek-count">{badgeCount}</span>
          )}
        </button>
      )}

      <div className="activity-notifications" role="dialog" aria-label="Notifications">
        <div className="activity-notifications-header">
          <div className="activity-notifications-title">
            <span className="activity-notifications-live" aria-hidden="true" />
            <strong>Notification</strong>
            <span className="activity-notifications-count">{badgeCount}</span>
          </div>
          <button
            type="button"
            className="activity-notifications-arrow"
            onClick={hidePopup}
            title="Hide"
            aria-label="Hide notifications"
          >
            <BiChevronRight size={24} />
          </button>
        </div>

        <div className="activity-notifications-body" ref={listRef}>
          {error ? (
            <p className="activity-notifications-empty">{error}</p>
          ) : !hasContent ? (
            <p className="activity-notifications-empty">No recent activity yet.</p>
          ) : (
            <>
              {activePlantGroup && (
                <div className="activity-pending-section" key={activePlantGroup.plant_id}>
                  <div className="activity-pending-heading">
                    Pending check-in
                    <span className="activity-pending-heading-count">{pendingTotal}</span>
                    {pendingGroups.length > 1 && (
                      <span className="activity-pending-rotate-hint">{plantPosition} · switches in 10s</span>
                    )}
                  </div>
                  <div className="activity-pending-plant">
                    <div className="activity-pending-plant-name">
                      {activePlantGroup.plant_name}
                      <span>{activePlantGroup.count || activePlantGroup.vehicles?.length || 0}</span>
                    </div>
                    <ul className="activity-notifications-list activity-pending-list">
                      {(activePlantGroup.vehicles || []).map((vehicle) => (
                        <li
                          key={vehicle.id}
                          className="activity-notifications-item notif-kind-waiting is-entering"
                        >
                          <span className="activity-notifications-badge">Pending</span>
                          <span className="activity-notifications-message">
                            {vehicle.message || vehicle.vehicle_no}
                            {vehicle.dc_no ? ` · DC ${vehicle.dc_no}` : ''}
                          </span>
                          <span className="activity-notifications-module">GATE</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {items.length > 0 && (
                <ul className="activity-notifications-list">
                  {items.map((item) => (
                    <li
                      key={item.id}
                      className={`activity-notifications-item ${kindClass(item.kind)} ${
                        item._enter ? 'is-entering' : ''
                      }`}
                    >
                      <span className="activity-notifications-badge">{kindLabel(item.kind)}</span>
                      <span className="activity-notifications-message">{item.message}</span>
                      {item.module ? (
                        <span className="activity-notifications-module">{item.module}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActivityNotifications;
